/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import classnames from 'classnames';
import format from 'date-format';
import api from 'utils/api';
import useCsrf from 'utils/csrf';
import scrollTo from 'utils/scrollTo';
import useMounted from 'utils/mounted';
import Step from './Step/Step';
import Receipt from './Receipt/Receipt';
import Image from './Image/Image';
import './Newsletter.scss';

function getDefaultState(steps) {
  const state = {};

  Object.keys(steps).forEach(key => {
    steps[key].form.fields.forEach(field => {
      if (typeof field.value === 'undefined') {
        if (field.type === 'choices' && field.multiple) {
          state[field.id] = [];
        } else if (field.id === 'confirm') {
          state[field.id] = false;
        } else if (field.type === 'boolean') {
          state[field.id] = false;
        } else if (field.type === 'booleanList') {
          state[field.id] = false;

          field.options.forEach(option => {
            state[option.id] = false;
          });
        } else {
          state[field.id] = '';
        }
      } else if (Array.isArray(field.value)) {
        state[field.id] = [...field.value];
      } else if (typeof field.value === 'object') {
        state[field.id] = { ...field.value };
      } else {
        state[field.id] = field.value;
      }
    });
  });
  return state;
}

function getPostBody(state) {
  const body = {};
  const demDataFields = [];

  Object.keys(state).forEach(key => {
    if (key.indexOf('demDataFields-') === -1) {
      if (['confirm', 'properties-all'].indexOf(key) === -1) {
        body[key] = state[key];
      }
    } else {
      const keys = key.split('-');
      let Value = state[key];

      if (keys[1] === 'Leie_utloepsdato') {
        try {
          const date = format.parse('yyyy-MM-dd', Value);
          Value = format('dd.MM.yyyy', date);
        } catch (error) {}
      }

      demDataFields.push({
        Key: keys[1],
        Value,
      });
    }
  });

  return {
    ...body,
    demDataFields,
  };
}

function getStep(steps, state, current) {
  const stepsWithValues = JSON.parse(JSON.stringify(steps));

  Object.keys(steps).forEach(key => {
    steps[key].form.fields.forEach(({ id, type }, index) => {
      if (typeof state[id] !== 'undefined') {
        stepsWithValues[key].form.fields[index].value = state[id];

        if (type === 'booleanList') {
          stepsWithValues[key].form.fields[index].options = steps[
            key
          ].form.fields[index].options.map(option => {
            if (typeof state[option.id] !== 'undefined') {
              return { ...option, value: state[option.id] };
            }
            return option;
          });
        }
      }
      if (id === 'demDataFields-Leie_utloepsdato') {
        if (state['demDataFields-Leier_i_dag']) {
          stepsWithValues[key].form.fields[index].required = true;
        }
      }
    });
  });

  return stepsWithValues[current];
}

function getSelectedRoute(data) {
  if (data['demDataFields-Leie']) {
    return 'rent';
  }

  if (data['demDataFields-Transaksjoner']) {
    return 'transactions';
  }

  if (data['demDataFields-Media']) {
    return 'media';
  }

  return null;
}

/**
 * This is the Newsletter component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Newsletter({
  heading,
  help,
  text,
  action,
  steps,
  image,
  receipt,
  extra,
  formErrors,
  labels,
}) {
  const node = useRef(null);
  const mounted = useMounted();
  const [csrf, actions] = useCsrf();
  const [step, setStep] = useState('personalia');
  const [data, setData] = useState(getDefaultState(steps));
  const [state, setState] = useState(null);
  const route = getSelectedRoute(data);

  useEffect(() => {
    node.current.dispatchEvent(new Event('react-ready', { bubbles: true }));
    actions.refresh();
  }, []);

  useEffect(() => {
    if (state === 'success') {
      setData(getDefaultState(steps));
      setStep('personalia');
    }
  }, [state]);

  function onMount() {
    if (mounted) {
      const blockNode = node.current.closest('.block-newsletter');
      scrollTo(blockNode, true);
    }
  }

  function onChange({ id, value }) {
    setData(prev => {
      if (id === 'demDataFields-Transaksjoner') {
        if (value === true) {
          return {
            ...prev,
            'demDataFields-Transaksjoner_kjoep': true,
            'demDataFields-Transaksjoner_salg': true,
            [id]: value,
          };
        }

        return {
          ...prev,
          'demDataFields-Transaksjoner_kjoep': false,
          'demDataFields-Transaksjoner_salg': false,
          [id]: value,
        };
      }

      if (
        [
          'demDataFields-Transaksjoner_kjoep',
          'demDataFields-Transaksjoner_salg',
        ].indexOf(id) > -1
      ) {
        if (value === true) {
          return {
            ...prev,
            'demDataFields-Transaksjoner': true,
            [id]: value,
          };
        }
      }

      if (id === 'demDataFields-Leie') {
        if (value === true) {
          return {
            ...prev,
            'demDataFields-Leie_leie': true,
            'demDataFields-Leie_utleie': true,
            [id]: value,
          };
        }

        return {
          ...prev,
          'demDataFields-Leie_leie': false,
          'demDataFields-Leie_utleie': false,
          [id]: value,
        };
      }

      if (
        ['demDataFields-Leie_leie', 'demDataFields-Leie_utleie'].indexOf(id) >
        -1
      ) {
        if (value === true) {
          return {
            ...prev,
            'demDataFields-Leie': true,
            [id]: value,
          };
        }
      }

      if (id === 'properties-all' && value === true) {
        const properties = {};

        steps.properties.form.fields.forEach(({ id }) => {
          properties[id] = true;
        });

        return {
          ...prev,
          ...properties,
          [id]: value,
        };
      }

      const propertyKeys = steps.properties.form.fields.map(({ id }) => id);

      if (propertyKeys.indexOf(id) && !value) {
        return {
          ...prev,
          'properties-all': false,
          [id]: value,
        };
      }

      return {
        ...prev,
        [id]: value,
      };
    });
  }

  function onSubmit() {
    const subscription = getPostBody({
      ...extra,
      ...data,
    });

    setState('loading');

    api(action, 'POST', { [csrf.name]: csrf.value, subscription })
      .then(() => {
        setState('success');
        actions.refresh();
      })
      .catch(() => {
        setState('error');
        actions.refresh();
      });
  }

  function renderHeading() {
    if (!heading) return null;
    if (state == 'success') return null;
    if (step !== 'personalia') return null;

    return <h2 className="animate-1">{heading}</h2>;
  }

  function renderHelp() {
    if (!help) return null;
    if (state == 'success') return null;
    if (step !== 'personalia') return null;

    return (
      <div
        className="help animate-2"
        dangerouslySetInnerHTML={{ __html: help }}
      />
    );
  }

  function renderText() {
    if (!text) return null;
    if (state == 'success') return null;
    if (step !== 'personalia') return null;

    return <div className="text">{text}</div>;
  }

  function renderContent() {
    if (state === 'success') {
      return (
        <CSSTransition key="success" timeout={600}>
          <Receipt
            {...receipt}
            labels={labels}
            onMount={onMount}
            onBack={() => {
              setState(null);
            }}
          />
        </CSSTransition>
      );
    }

    return (
      <CSSTransition key={step} timeout={600} exit={false}>
        <Step
          id={step}
          route={route}
          {...getStep(steps, data, step)}
          loading={state === 'loading'}
          errors={formErrors}
          onChange={onChange}
          onSubmit={onSubmit}
          onMount={onMount}
          onNext={() => {
            setStep(current => {
              switch (current) {
                case 'personalia':
                  return 'job';
                case 'job':
                  if (data['demDataFields-Leie']) {
                    return 'rent';
                  }

                  if (data['demDataFields-Transaksjoner']) {
                    return 'properties';
                  }

                  return current;
                case 'rent':
                  return 'properties';
                default:
                  return current;
              }
            });
          }}
          onPrev={() => {
            setStep(current => {
              switch (current) {
                case 'job':
                  return 'personalia';
                case 'properties':
                  if (data['demDataFields-Leie']) {
                    return 'rent';
                  }
                  return 'job';
                case 'rent':
                  return 'job';
                default:
                  return current;
              }
            });
          }}
        />
      </CSSTransition>
    );
  }

  function renderImage() {
    if (!image) return null;
    if (state == 'success') return null;
    if (step !== 'personalia') return null;

    return <Image {...image} />;
  }

  return (
    <>
      <div
        className={classnames('newsletter-content', {
          'with-image': !!image,
        })}
      >
        {renderHeading()}
        {renderHelp()}
        <div className="animate-3">
          <div ref={node} className="newsletter">
            <TransitionGroup>{renderContent()}</TransitionGroup>
          </div>
        </div>
        {renderText()}
      </div>
      {renderImage()}
    </>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Newsletter.propTypes = {
  action: PropTypes.string.isRequired,
  heading: PropTypes.string,
  help: PropTypes.string,
  text: PropTypes.string,
  image: PropTypes.object,
  extra: PropTypes.object.isRequired,
  steps: PropTypes.object.isRequired,
  receipt: PropTypes.shape({
    heading: PropTypes.string,
    text: PropTypes.string,
    share: PropTypes.object.isRequired,
  }).isRequired,
  submit: PropTypes.shape({
    color: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  formErrors: PropTypes.object,
  error: PropTypes.string,
  success: PropTypes.string,
  labels: PropTypes.object,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Newsletter.defaultProps = {
  steps: {},
  extra: {},
  receipt: {
    heading: 'Thanks for signing up.',
    text: null,
    share: {
      url: location.href,
    },
  },
  submit: {
    color: 'red',
    text: 'Submit',
  },
  error: 'An error occured, please try again.',
  success: 'Thanks for signing up.',
};

export default Newsletter;
